import { Popover } from 'antd';
import { TextAreaRef } from 'antd/lib/input/TextArea';
import React, { FC, useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { ProjectId, Team, User } from 'api/models';
import { Button } from 'components/atoms/button';
import {
  CustomDueDatePicker,
  DateValue,
} from 'components/atoms/due-date-picker';
import { OutsideClickHandler } from 'components/atoms/outside-click-handler';
import { RTE } from 'components/atoms/rte';
import { isRelatedEl } from 'components/molecules/new-task-popup/is-related-element';
import {
  NegativeMarginWrapper,
  PopupContent,
  TaskActions,
  TaskTitle,
} from 'components/molecules/new-task-popup/new-task-popup.style';
import { TaskAssigneePicker } from 'components/molecules/squad-picker/assignee-picker';
import { Option } from 'components/molecules/squad-picker/types';
import { Task } from 'data/tasks';
import { useTranslation } from 'hooks/use-translation';
import { useCreateTaskMutation } from 'queries';

export const CreateTaskPopover: FC<{
  projectId: number;
  phaseId: number;
  visible: boolean;
  toggle: (value: boolean) => void;
  isPrivate: boolean;
}> = ({ projectId, phaseId, toggle, visible, isPrivate }) => {
  const { mutate, isLoading: isCreateInProgress } = useCreateTaskMutation(
    ProjectId(projectId),
  );

  const [title, setTitle] = useState('');
  const inputRef = useRef<TextAreaRef>(null);
  const [assignee, setAssignee] = useState<Option[]>([]);

  const [dueDate, setDueDate] = useState<DateValue>();
  const [startDate, setStartDate] = useState<DateValue>();
  const [description, setDescription] = useState('');

  const taskDescLabel = useTranslation('task.description.placeholder');
  const taskNameLabel = useTranslation('task_name');

  const hasFormData = () =>
    title ||
    assignee.length > 0 ||
    dueDate ||
    startDate ||
    description.length > 0;

  const cleanupForm = () => {
    setTitle('');
    setAssignee([]);
    setDueDate(undefined);
    setStartDate(undefined);
    setDescription('');
  };

  const closeForm = () => {
    cleanupForm();
    toggle(false);
  };

  const handleDueDateChange = ([_startDate, _dueDate]) => {
    setDueDate(_dueDate);
    setStartDate(_startDate);
  };

  useEffect(() => {
    window.setTimeout(() => inputRef.current?.focus(), 100);
  }, []);

  const createTask = (_task: Partial<Task>) => {
    const task = {
      ..._task,
      projectPhase: {
        projectPhaseId: phaseId,
      },
    };

    mutate(task, {
      onSettled: () => closeForm(),
    });
  };

  const onFormSubmitTask = () => {
    const userPayload = (o: Option) =>
      ({ ...o, userId: o.extra.id } as unknown);
    const teamPayload = (o: Option) =>
      ({ ...o, teamId: o.extra.id } as unknown);
    const users = assignee
      .filter((o) => o.extra.type === 'user')
      .map(userPayload);

    const teams = assignee
      .filter((o) => o.extra.type === 'team')
      .map(teamPayload);

    const newTask = {
      dueDate,
      startDate,
      taskName: title,
      taskDescription: description,
      assignee: {
        users: users as User[],
        teams: teams as Team[],
      },
    };

    createTask(newTask);
  };

  return (
    <Popover
      visible={visible}
      destroyTooltipOnHide
      trigger="click"
      placement="right"
      content={
        <PopupContent>
          <OutsideClickHandler
            onOutsideClick={() => {
              if (!hasFormData()) {
                closeForm();
              }
            }}
            allowed={isRelatedEl}
          >
            <TaskTitle
              autoFocus
              ref={inputRef}
              placeholder={taskNameLabel}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              onBlur={(e) => {
                setTitle(e.target.value);
              }}
              autoSize={{ minRows: 1, maxRows: 6 }}
            />
            <NegativeMarginWrapper>
              <TaskAssigneePicker
                isPrivate={isPrivate}
                projectId={ProjectId(projectId)}
                value={assignee}
                onChange={setAssignee}
                startDate={startDate}
                dueDate={dueDate}
              />
            </NegativeMarginWrapper>
            <NegativeMarginWrapper>
              <CustomDueDatePicker
                block
                startDate={startDate}
                dueDate={dueDate}
                onDateChange={handleDueDateChange}
              />
            </NegativeMarginWrapper>
            <RTE
              projectId={ProjectId(projectId)}
              onChange={(data) => {
                setDescription(data);
              }}
              data={description}
              onEnter={onFormSubmitTask}
              placeholder={taskDescLabel}
            />
            <TaskActions>
              <Button
                size="small"
                data-testid="submit"
                onClick={onFormSubmitTask}
                disabled={!title.length}
                type="primary"
                loading={isCreateInProgress}
              >
                <FormattedMessage id="create" />
              </Button>
              <Button size="small" onClick={closeForm} data-testid="cancel">
                <FormattedMessage id="cancel" />
              </Button>
            </TaskActions>
          </OutsideClickHandler>
        </PopupContent>
      }
    />
  );
};
